import * as Sentry from '@sentry/react';

import { shouldDropCapturedException, shouldDropSentryEvent } from './sentry-filters';

const dsn = process.env.SENTRY_DSN;

if (dsn) {
  Sentry.init({
    dsn,
    environment: process.env.NODE_ENV,
    release: process.env.APP_VERSION,
    denyUrls: [/ravenjs\.com/i],
    ignoreErrors: [
      'AbortError',
      'No refresh token specified.',
      /Non-Error exception captured with keys: detail, error_code/,
      /Object captured as (promise rejection|exception) with keys/,
      /ResizeObserver loop completed with undelivered notifications/,
      /Unable to preload CSS/,
      /Loading CSS chunk \d+ failed/,
      /Access is denied for this document/,
      /\[object Promise\]/,
      /getCharacterList/,
      /delete range from database without an in-progress transaction/,
      /Registration does not have an active worker/,
      /^Load failed$/,
      /^Failed to fetch$/,
      /Failed to fetch \(/,
    ],
    beforeSend(event, hint) {
      if (shouldDropSentryEvent(event, hint)) return null;

      return event;
    },
  });
}

function normalizeCaptureException(exception: unknown): Error {
  if (exception instanceof Error) return exception;
  if (typeof exception === 'string') return new Error(exception);

  if (exception && typeof exception === 'object') {
    const record = exception as Record<string, unknown>;
    const message = typeof record.message === 'string'
      ? record.message
      : typeof record.detail === 'string'
        ? record.detail
        : JSON.stringify(exception);

    const error = new Error(message);
    if (typeof record.name === 'string') error.name = record.name;

    return error;
  }

  return new Error('Unknown error');
}

interface SentryUser {
  id?: string | number;
  email?: string;
  username?: string;
}

export function captureException(
  exception: unknown,
  extra?: Record<string, unknown>,
  level?: Sentry.SeverityLevel,
) {
  if (!dsn) return;
  if (exception === undefined || exception === null) return;

  if (shouldDropCapturedException(exception)) return;

  const normalized = normalizeCaptureException(exception);

  Sentry.captureException(normalized, {
    extra: {
      ...extra,
      ...(exception !== normalized && exception && typeof exception === 'object'
        ? { originalException: exception }
        : {}),
    },
    level,
  });
}

export function setUser(user: SentryUser | null) {
  if (!dsn) return;

  if (!user) {
    Sentry.setUser(null);
    return;
  }

  Sentry.setUser({
    id: user.id != null ? String(user.id) : undefined,
    email: user.email,
    username: user.username,
  });
}

export function setContext(name: string, context: Record<string, unknown> | null) {
  if (!dsn) return;

  Sentry.setContext(name, context);
}
